import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { discoverColoredBlocks } from '../utils/discoverColoredBlocks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import { UniversalJsonRenderer } from '../components/UniversalJsonRenderer';
import { WysiwygPageContainer, SectionControlPanel } from '../components/pdf-export';
import { usePdfExportState, ReportSection } from '../hooks/usePdfExportState';
import { usePdfPageLayout } from '../hooks/usePdfPageLayout';
import { Settings, RotateCcw, Printer } from 'lucide-react';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github.css';

interface CompanyBranding {
  logoUrl?: string | null;
  logoS3Key?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  logoOnAllPages?: boolean;
  companyDisplayName?: string | null;
  headerText?: string | null;
  footerText?: string | null;
}

interface WorkflowReportData {
  content?: string;
  workflowType: string;
  documents: Array<{
    id: string;
    name: string;
    policyType?: string;
    size?: number;
    itemType?: string;
  }>;
  customerId?: string;
  customerName?: string;
  citations?: Array<any>;
  universalJsonResponse?: any;
  isUniversalJson?: boolean;
  reportId?: string;
  reportTitle?: string;
  projectId?: string;
  createdAt?: Date;
  hideCitations?: boolean;
  updatedAt?: Date;
  additionalContext?: string;
  isRegenerated?: boolean;
}

const PdfExportPreview: React.FC = () => {
  const [workflowData, setWorkflowData] = useState<WorkflowReportData | null>(null);
  const [sections, setSections] = useState<ReportSection[]>([]);
  // Colored sub-blocks discovered structurally from the rendered report DOM.
  const [discoveredBlocks, setDiscoveredBlocks] = useState<ReportSection[]>([]);
  const [editedContent, setEditedContent] = useState<Record<string, string>>({});
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const reportContentRef = useRef<HTMLDivElement>(null);

  // Initialize export state hook (will be populated after sections load)
  const exportState = usePdfExportState({
    initialSections: sections,
    storageKey: 'pdf-export-state'
  });

  // Calculate content dimensions
  const contentHeight = useMemo(() => {
    return exportState.contentDimensions.height;
  }, [exportState.contentDimensions.height]);

  // Key that forces the report tree to re-render. Block removal is deliberately
  // NOT part of this key (it is a DOM overlay), so toggling a block does not
  // remount the renderer and therefore preserves inline edits.
  const rendererKey = useMemo(
    () => `${JSON.stringify(exportState.selectedSections)}-${exportState.sectionOrder.join(',')}-${Array.from(exportState.manualPageBreaks).join(',')}-${Array.from(exportState.keepTogether).join(',')}`,
    [exportState.selectedSections, exportState.sectionOrder, exportState.manualPageBreaks, exportState.keepTogether]
  );

  // Real sections (drive the renderer + pagination) plus discovered colored
  // blocks (display-only entries the control panel renders as removable children).
  const allSections = useMemo(
    () => [...sections, ...discoveredBlocks],
    [sections, discoveredBlocks]
  );

  // Initialize page layout calculation
  const pageLayout = usePdfPageLayout({
    contentContainerRef: reportContentRef,
    sectionOrder: exportState.sectionOrder,
    selectedSections: exportState.selectedSections,
    manualPageBreaks: exportState.manualPageBreaks,
    keepTogether: exportState.keepTogether,
    pageContentHeight: contentHeight,
    headerHeight: logoDataUrl ? 80 : 0
  });

  // Initialize section positions from flow layout
  useEffect(() => {
    if (pageLayout.pages.length > 0 && exportState.sectionPositions.size === 0) {
      for (const page of pageLayout.pages) {
        for (const item of page.items) {
          if (!exportState.selectedSections[item.sectionId]) continue;
          if (item.isContinuation) continue;
          exportState.initializeSectionPosition(item.sectionId, page.pageNumber, item.offsetY);
        }
      }
    }
  }, [pageLayout.pages, exportState.selectedSections, exportState.initializeSectionPosition, exportState.sectionPositions.size]);

  // Discover removable colored sub-blocks from the rendered report DOM.
  // Re-runs whenever the report tree re-renders (rendererKey changes), since
  // a remount recreates the nodes and drops the data-block-id tags.
  useLayoutEffect(() => {
    if (!workflowData?.isUniversalJson) return;

    let cancelled = false;
    let attempts = 0;

    const run = () => {
      if (cancelled || !reportContentRef.current) return;
      const blocks = discoverColoredBlocks(reportContentRef.current);
      // The report may still be settling; retry a few times if nothing found.
      if (blocks.length === 0 && attempts < 5) {
        attempts++;
        setTimeout(run, 150 * attempts);
        return;
      }
      setDiscoveredBlocks(blocks);
    };

    run();
    return () => { cancelled = true; };
  }, [workflowData, rendererKey]);

  // Apply colored-block removal as a DOM overlay on the measurement container
  // (the DOM that window.print() outputs), then recompute pagination so the
  // on-screen preview and page count stay correct.
  useLayoutEffect(() => {
    const root = reportContentRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('[data-block-id]').forEach(el => {
      const id = el.dataset.blockId!;
      el.style.display = exportState.removedBlocks.has(id) ? 'none' : '';
    });
    pageLayout.measureSections();
  }, [exportState.removedBlocks, exportState.blockRevision, discoveredBlocks, pageLayout.measureSections]);

  // Handle drag end - update position
  const handleDragEnd = (sectionId: string, deltaX: number, deltaY: number, targetPageNumber?: number) => {
    const currentPos = exportState.sectionPositions.get(sectionId);
    if (!currentPos) return;

    const newX = Math.max(0, currentPos.x + deltaX);
    const newY = Math.max(0, currentPos.y + deltaY);
    const newPage = targetPageNumber || currentPos.pageNumber;

    exportState.updateSectionPosition(sectionId, {
      x: newX,
      y: newY,
      pageNumber: newPage
    });
  };

  // Load data from URL parameters and localStorage on mount
  useEffect(() => {
    // Force light mode by removing dark class from document
    document.documentElement.classList.remove('dark');
    document.body.classList.remove('dark');

    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const exportKey = urlParams.get('key');
    const token = urlParams.get('token');

    console.log('📄 PdfExportPreview: URL params:', { hasKey: !!exportKey, hasToken: !!token });

    // Get export data from localStorage using the key
    const exportData = exportKey ? localStorage.getItem(exportKey) : null;

    if (exportData) {
      try {
        const parsedData = JSON.parse(exportData);

        // Debug logging
        console.log('📄 PdfExportPreview: Loaded export data:', {
          hasWorkflowData: !!parsedData.workflowData,
          isUniversalJson: parsedData.workflowData?.isUniversalJson,
          hasUniversalJsonResponse: !!parsedData.workflowData?.universalJsonResponse,
          sectionsCount: parsedData.sections?.length,
          workflowType: parsedData.workflowData?.workflowType,
          hasToken: !!token
        });

        setWorkflowData(parsedData.workflowData);

        // Store token from URL parameter
        if (token) {
          console.log('📄 PdfExportPreview: Token received from URL');
          setAuthToken(token);
        }

        // Clean up - remove the export data after loading
        if (exportKey) {
          localStorage.removeItem(exportKey);
        }

        // Use sections directly from WorkflowReport
        const allSections = [...parsedData.sections];
        console.log('📄 PdfExportPreview: Loaded sections:', allSections.map((s: ReportSection) => ({ id: s.id, title: s.title, isChild: s.isChild })));

        setSections(allSections);

        // Clear sessionStorage after loading
        sessionStorage.removeItem('pdfExportData');
      } catch (error) {
        console.error('Failed to load export data:', error);
        alert('Failed to load report data for export');
      }
    } else {
      alert('No report data found. Please try exporting again.');
    }
  }, []);

  // Fetch company branding data
  useEffect(() => {
    if (!authToken) {
      console.log('📄 PdfExportPreview: Waiting for auth token...');
      return;
    }

    console.log('📄 PdfExportPreview: Auth token available, fetching branding...');
    const fetchBranding = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const url = `${apiUrl}/api/admin/company`;
        console.log('📄 PdfExportPreview: Fetching branding from:', url);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        console.log('📄 PdfExportPreview: Branding response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('📄 PdfExportPreview: Branding data received:', data);
          if (data.branding) {
            setBranding(data.branding);
            console.log('📄 PdfExportPreview: Branding set, logoUrl:', data.branding.logoUrl);
          } else {
            console.warn('📄 PdfExportPreview: No branding object in response');
          }
        } else {
          console.error('📄 PdfExportPreview: Branding fetch failed with status:', response.status);
        }
      } catch (error) {
        console.error('📄 PdfExportPreview: Failed to load company branding:', error);
      }
    };

    fetchBranding();
  }, [authToken]);

  // Fetch and convert logo to data URL
  useEffect(() => {
    console.log('📄 PdfExportPreview: Logo fetch effect triggered, branding:', branding, 'authToken:', !!authToken);
    const fetchLogo = async () => {
      if (!branding?.logoUrl) {
        console.log('📄 PdfExportPreview: No logoUrl in branding, setting null');
        setLogoDataUrl(null);
        return;
      }

      console.log('📄 PdfExportPreview: Starting logo fetch, logoUrl:', branding.logoUrl);

      // If it's already a data URL, use it directly
      if (branding.logoUrl.startsWith('data:')) {
        console.log('📄 PdfExportPreview: logoUrl is already a data URL');
        setLogoDataUrl(branding.logoUrl);
        return;
      }

      if (!authToken) {
        console.warn('📄 PdfExportPreview: No auth token available for logo fetch');
        setLogoDataUrl(null);
        return;
      }

      // If it's a relative path (streaming endpoint), fetch it with auth token
      if (!branding.logoUrl.startsWith('http')) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const url = `${apiUrl}${branding.logoUrl}`;
          console.log('📄 PdfExportPreview: Fetching logo from:', url);

          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });

          console.log('📄 PdfExportPreview: Logo fetch response status:', response.status);

          if (response.ok) {
            const blob = await response.blob();
            console.log('📄 PdfExportPreview: Logo blob received, size:', blob.size);
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoDataUrl(reader.result as string);
              console.log('📄 PdfExportPreview: Logo converted to data URL, length:', (reader.result as string)?.length);
            };
            reader.readAsDataURL(blob);
          } else {
            console.error('📄 PdfExportPreview: Logo fetch failed with status:', response.status);
          }
        } catch (err) {
          console.error('📄 PdfExportPreview: Failed to fetch logo:', err);
        }
        return;
      }

      // For absolute URLs, just use them directly
      console.log('📄 PdfExportPreview: Using absolute URL directly');
      setLogoDataUrl(branding.logoUrl);
    };

    fetchLogo();
  }, [branding?.logoUrl, authToken]);

  // Handle edit mode toggle
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Handle inline editing
  const handleSectionEdit = (sectionId: string, content: string) => {
    setEditedContent(prev => ({
      ...prev,
      [sectionId]: content
    }));
  };

  // Handle export to PDF
  const handleExport = () => {
    // Exit edit mode first so printing uses the standard (non-editing) layout
    // path. Inline edits live in the measurement-container DOM and persist.
    if (isEditMode) {
      setIsEditMode(false);
    }

    const doPrint = () => {
      // Hide controls for printing
      const controlsElement = document.getElementById('export-controls');
      if (controlsElement) {
        controlsElement.style.display = 'none';
      }

      // Trigger browser print dialog
      window.print();

      // Restore controls after print dialog
      setTimeout(() => {
        if (controlsElement) {
          controlsElement.style.display = 'flex';
        }
      }, 100);
    };

    // Give React a tick to restore the paginated view before printing.
    if (isEditMode) {
      setTimeout(doPrint, 150);
    } else {
      doPrint();
    }
  };

  // Extract section content from report
  const renderSectionContent = (sectionId: string): React.ReactNode => {
    if (!workflowData) return null;

    // Check if content has been edited
    if (editedContent[sectionId]) {
      return (
        <div
          className={`editable-section ${editingSection === sectionId ? 'editing' : ''}`}
          contentEditable={editingSection === sectionId}
          suppressContentEditableWarning
          onClick={() => setEditingSection(sectionId)}
          onBlur={(e) => {
            setEditingSection(null);
            handleSectionEdit(sectionId, e.currentTarget.innerHTML);
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(editedContent[sectionId]) }}
        />
      );
    }

    // For Universal JSON reports, render specific sections
    if (workflowData.isUniversalJson && workflowData.universalJsonResponse) {
      const data = workflowData.universalJsonResponse;

      switch (sectionId) {
        case 'executive-summary':
          return (
            <div
              className={`editable-section ${editingSection === sectionId ? 'editing' : ''}`}
              contentEditable={editingSection === sectionId}
              suppressContentEditableWarning
              onClick={() => setEditingSection(sectionId)}
              onBlur={(e) => {
                setEditingSection(null);
                handleSectionEdit(sectionId, e.currentTarget.innerHTML);
              }}
            >
              {data.executiveSummary && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight, rehypeKatex]}>
                  {data.executiveSummary}
                </ReactMarkdown>
              )}
            </div>
          );

        default:
          return (
            <div className="text-gray-600 italic">
              Section rendering not yet implemented for: {sectionId}
            </div>
          );
      }
    }

    // For Markdown reports, try to extract the section
    if (typeof workflowData.content === 'string') {
      const section = sections.find(s => s.id === sectionId);
      if (!section) return null;

      // Try to extract section content using regex
      const sectionRegex = new RegExp(
        `^#+\\s*${section.title}\\s*$([\\s\\S]*?)(?=^#+\\s|$)`,
        'mi'
      );
      const match = workflowData.content.match(sectionRegex);

      if (match && match[1]) {
        return (
          <div
            className={`editable-section ${editingSection === sectionId ? 'editing' : ''}`}
            contentEditable={editingSection === sectionId}
            suppressContentEditableWarning
            onClick={() => setEditingSection(sectionId)}
            onBlur={(e) => {
              setEditingSection(null);
              handleSectionEdit(sectionId, e.currentTarget.innerHTML);
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight, rehypeKatex]}>
              {match[1].trim()}
            </ReactMarkdown>
          </div>
        );
      }
    }

    return null;
  };

  if (!workflowData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1993e5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report data...</p>
        </div>
      </div>
    );
  }

  const selectedCount = Object.values(exportState.selectedSections).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top Toolbar - Hidden during print */}
      <div
        id="export-controls"
        className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm print:hidden"
      >
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">PDF Export Preview</h1>
              <p className="text-sm text-gray-600">
                {isEditMode
                  ? 'Editing — click any text to change it. Removing colored blocks keeps your edits; re-selecting or reordering sections will reset them.'
                  : `WYSIWYG preview • ${pageLayout.totalPages} page${pageLayout.totalPages !== 1 ? 's' : ''} • ${selectedCount} sections selected`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Settings toggle */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg transition-colors ${
                  showSettings
                    ? 'bg-gray-200 text-gray-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                title="Page settings"
              >
                <Settings className="w-5 h-5" />
              </button>

              {/* Reset button */}
              <button
                onClick={exportState.resetToDefaults}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                title="Reset to defaults"
              >
                <RotateCcw className="w-5 h-5" />
              </button>

              {/* Edit mode toggle */}
              <button
                onClick={toggleEditMode}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isEditMode
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {isEditMode ? 'Exit Edit Mode' : 'Edit Content'}
              </button>

              {/* Export button */}
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-5 py-2 bg-[#1993e5] text-white rounded-lg font-medium hover:bg-[#1577c1] transition-colors"
              >
                <Printer className="w-4 h-4" />
                Export PDF
              </button>
            </div>
          </div>

          {/* Settings panel */}
          {showSettings && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-6">
              {/* Page size */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Size:</label>
                <select
                  value={exportState.pageSettings.size}
                  onChange={(e) => exportState.setPageSettings({ size: e.target.value as 'a4' | 'letter' })}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="a4">A4</option>
                  <option value="letter">Letter</option>
                </select>
              </div>

              {/* Orientation */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Orientation:</label>
                <select
                  value={exportState.pageSettings.orientation}
                  onChange={(e) => exportState.setPageSettings({ orientation: e.target.value as 'portrait' | 'landscape' })}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>

              {/* Margins */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Margins:</label>
                <select
                  value={exportState.pageSettings.margins}
                  onChange={(e) => exportState.setPageSettings({ margins: parseInt(e.target.value) })}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="10">10mm (Narrow)</option>
                  <option value="15">15mm (Normal)</option>
                  <option value="20">20mm (Wide)</option>
                  <option value="25">25mm (Extra Wide)</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden print:block">
        {/* Sidebar with section controls - Hidden during print */}
        <div className="w-80 border-r border-gray-200 print:hidden">
          <SectionControlPanel
            sections={allSections}
            selectedSections={exportState.selectedSections}
            sectionOrder={exportState.sectionOrder}
            manualPageBreaks={exportState.manualPageBreaks}
            keepTogether={exportState.keepTogether}
            sectionToPage={pageLayout.sectionToPage}
            onToggleSection={exportState.toggleSection}
            onToggleAll={exportState.toggleAllSections}
            onReorder={exportState.reorderSections}
            onTogglePageBreak={exportState.togglePageBreak}
            onToggleKeepTogether={exportState.toggleKeepTogether}
            removedBlocks={exportState.removedBlocks}
            onToggleBlock={exportState.toggleBlock}
          />
        </div>

        {/* WYSIWYG Preview Area */}
        <div className="flex-1 overflow-hidden print:overflow-visible">
          <WysiwygPageContainer
            pages={pageLayout.pages}
            pageDimensions={exportState.pageDimensions}
            marginPixels={exportState.marginPixels}
            zoom={exportState.zoom}
            onZoomChange={exportState.setZoom}
            logoDataUrl={logoDataUrl}
            logoPosition={branding?.logoPosition}
            companyName={branding?.companyDisplayName || undefined}
            headerText={branding?.headerText || undefined}
            footerText={branding?.footerText || undefined}
            sectionOrder={exportState.sectionOrder}
            selectedSections={exportState.selectedSections}
            sectionPositions={exportState.sectionPositions}
            onReorder={exportState.reorderSections}
            onDragEnd={handleDragEnd}
            editMode={isEditMode}
            revision={exportState.blockRevision}
          >
            {/* Report content. In edit mode this container (the print source) is
                made directly editable; inline edits live in the DOM and print. */}
            <div
              ref={reportContentRef}
              className={`force-light-mode ${isEditMode ? 'editable-report' : ''}`}
              contentEditable={isEditMode}
              suppressContentEditableWarning
            >
              {workflowData.isUniversalJson && workflowData.universalJsonResponse ? (
                // For Universal JSON reports, render using UniversalJsonRenderer
                <UniversalJsonRenderer
                  key={`${JSON.stringify(exportState.selectedSections)}-${exportState.sectionOrder.join(',')}-${Array.from(exportState.manualPageBreaks).join(',')}-${Array.from(exportState.keepTogether).join(',')}`}
                  data={workflowData.universalJsonResponse}
                  onCitationClick={() => {}}
                  hideCitations={true}
                  customerName={workflowData.customerName}
                  documents={workflowData.documents || []}
                  showExportControls={false}
                  selectedSections={exportState.selectedSections}
                  sectionOrder={exportState.sectionOrder}
                  manualPageBreaks={exportState.manualPageBreaks}
                  keepTogether={exportState.keepTogether}
                />
              ) : (
                // For Markdown reports, render sections individually
                exportState.orderedSections.map((section) => (
                  <div
                    key={section.id}
                    className={`section-${section.id} ${
                      !exportState.selectedSections[section.id] ? 'hidden' : ''
                    }`}
                    data-section-id={section.id}
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-sm max-w-none">
                      {renderSectionContent(section.id)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </WysiwygPageContainer>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        /* Force light mode - override any dark mode styles */
        .force-light-mode,
        .force-light-mode *  {
          color: inherit !important;
        }

        .force-light-mode {
          background-color: white !important;
          color: #111827 !important;
        }

        /* Override any dark mode background colors */
        .force-light-mode .dark\\:bg-gray-800,
        .force-light-mode .dark\\:bg-gray-900,
        .force-light-mode .dark\\:bg-slate-800 {
          background-color: white !important;
        }

        /* Override any dark mode text colors */
        .force-light-mode .dark\\:text-gray-100,
        .force-light-mode .dark\\:text-gray-200,
        .force-light-mode .dark\\:text-white {
          color: #111827 !important;
        }

        /* Override any dark mode border colors */
        .force-light-mode .dark\\:border-gray-700,
        .force-light-mode .dark\\:border-gray-600 {
          border-color: #e5e7eb !important;
        }

        @media print {
          /* Enable color printing */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .print\\:hidden {
            display: none !important;
          }

          .print\\:block {
            display: block !important;
          }

          .print\\:overflow-visible {
            overflow: visible !important;
          }

          /* Page settings */
          @page {
            margin: ${exportState.pageSettings.margins}mm;
            size: ${exportState.pageSettings.size} ${exportState.pageSettings.orientation};
          }

          /* Hide WYSIWYG decorations in print */
          .wysiwyg-toolbar,
          .wysiwyg-page > .absolute,
          .wysiwyg-page > div[class*="bg-gray-50"],
          .wysiwyg-page > div.absolute,
          .wysiwyg-pages-wrapper > div:last-child:not(.wysiwyg-page) {
            display: none !important;
          }

          .wysiwyg-page {
            box-shadow: none !important;
            width: 100% !important;
            min-height: auto !important;
          }

          .wysiwyg-page-content {
            padding: 0 !important;
          }

          .wysiwyg-pages-container {
            padding: 0 !important;
            background: white !important;
          }

          .wysiwyg-pages-wrapper {
            transform: none !important;
            width: 100% !important;
          }

          /* Ensure proper page breaks */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
            page-break-inside: avoid;
            break-after: avoid;
            break-inside: avoid;
          }

          table, figure, .matrix-container, .risk-matrix, .coverage-matrix {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Prevent orphans and widows */
          p {
            orphans: 3;
            widows: 3;
          }

          /* Sections with keep-together flag */
          [data-keep-together="true"] {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Manual page breaks */
          [data-page-break-after="true"] {
            page-break-after: always !important;
            break-after: page !important;
          }

          /* Force light mode colors in print */
          .force-light-mode {
            background-color: white !important;
            color: #111827 !important;
          }

          /* Preserve table colors */
          table {
            border-collapse: collapse !important;
          }

          th {
            background-color: #f3f4f6 !important;
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Preserve all background colors */
          .bg-blue-50, .bg-blue-100, .bg-blue-200,
          .bg-green-50, .bg-green-100, .bg-green-200,
          .bg-yellow-50, .bg-yellow-100, .bg-yellow-200,
          .bg-red-50, .bg-red-100, .bg-red-200,
          .bg-gray-50, .bg-gray-100, .bg-gray-200 {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Remove all scrollbars and overflow for print */
          .overflow-x-auto,
          .overflow-y-auto,
          .overflow-auto,
          [class*="overflow"] {
            overflow: visible !important;
            overflow-x: visible !important;
            overflow-y: visible !important;
          }

          /* Scale tables and matrices to fit page width */
          .analysis-matrix,
          .matrix-container,
          .risk-matrix,
          .coverage-matrix,
          .heat-map,
          [class*="matrix"],
          table {
            overflow: visible !important;
            width: 100% !important;
            max-width: 100% !important;
            font-size: 9pt !important;
          }

          /* Shrink table cells to fit */
          .analysis-matrix td,
          .analysis-matrix th,
          .matrix-table td,
          .matrix-table th,
          table td,
          table th {
            padding: 4px 6px !important;
            font-size: 8pt !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
          }

          /* Reduce overall content size for better page fit */
          .universal-json-report {
            font-size: 10pt !important;
          }

          .universal-json-report h2 {
            font-size: 14pt !important;
          }

          .universal-json-report h3 {
            font-size: 12pt !important;
          }
        }

        /* Constrain content width to match print area */
        .wysiwyg-page-content .universal-json-report,
        .wysiwyg-page-content .force-light-mode {
          max-width: 100%;
          overflow: hidden;
        }

        /* Styles for editable report in edit mode */
        .editable-report {
          cursor: text;
          outline: 3px dashed #1993e5;
          outline-offset: -3px;
          padding: 4px;
        }

        .editable-report:focus {
          outline: 3px solid #1993e5;
          background-color: #fffbeb;
        }

        .editable-report * {
          cursor: text !important;
        }

        /* Styles for editable sections in edit mode */
        .editable-section {
          cursor: text;
          border-radius: 4px;
          transition: all 0.2s;
          min-height: 2em;
          padding: 8px;
        }

        .editable-section:hover {
          background-color: #f9fafb;
          outline: 1px dashed #1993e5;
        }

        .editable-section.editing {
          background-color: #fff;
          outline: 2px solid #1993e5;
          box-shadow: 0 0 0 3px rgba(25, 147, 229, 0.1);
        }

        /* Ensure markdown content looks good */
        .prose {
          color: #374151;
        }

        .prose h1, .prose h2, .prose h3, .prose h4 {
          color: #111827;
          font-weight: 600;
        }

        .prose ul, .prose ol {
          padding-left: 1.5em;
        }

        .prose table {
          width: 100%;
          border-collapse: collapse;
        }

        .prose table th, .prose table td {
          border: 1px solid #e5e7eb;
          padding: 0.5em;
        }

        .prose table th {
          background-color: #f3f4f6;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default PdfExportPreview;
